import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import CircleRounded from '@mui/icons-material/CircleRounded';
import { useCallback } from 'react';
import { useFetch } from '../../../hooks/useFecth';
import { useReconciliationSocket } from '../../../hooks/useReconciliationSocket';

function formatDateTime(value) {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function money(value) {
    return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function jobTitle(job) {
    if (job.name === 'awin-transaction-notification') return 'Aviso da Awin';
    if (job.broker === 'lomadee') return 'Verificação Lomadee';
    return 'Verificação Awin';
}

function jobState(state) {
    if (state === 'completed') return { label: 'Feita', color: 'success' };
    if (state === 'failed') return { label: 'Falhou', color: 'error' };
    if (state === 'active') return { label: 'Rodando agora', color: 'info' };
    if (state === 'delayed' || state === 'waiting') return { label: 'Na fila', color: 'default' };
    return { label: state || '—', color: 'default' };
}

function itemStatus(status) {
    if (status === 'VALIDATED') return 'validada';
    if (status === 'REJECTED') return 'recusada';
    if (status === 'PENDING') return 'pendente';
    return status || 'atualizada';
}

function describeItem(item) {
    if (item.action === 'updated') {
        const bits = [`Resgate #${item.rescueId}`];
        if (item.saleValue) bits.push(`compra ${money(item.saleValue)}`);
        bits.push(itemStatus(item.status));
        return bits.join(' · ');
    }
    if (item.reason === 'no-rescue') {
        return `Venda com código, mas sem resgate nosso${item.saleValue ? ` · ${money(item.saleValue)}` : ''}`;
    }
    if (item.reason === 'no-id') {
        return `Resgate #${item.rescueId} encontrado, mas a venda veio sem número`;
    }
    return 'Venda não atualizada';
}

function JobCard({ job }) {
    const state = jobState(job.state);
    const result = job.result || {};
    const items = Array.isArray(result.items) ? result.items : [];
    const updated = items.filter(item => item.action === 'updated');
    const skipped = items.filter(item => item.action !== 'updated');
    const when = job.finishedOn || job.processedOn || job.timestamp;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700}>{jobTitle(job)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {formatDateTime(when)}
                    </Typography>
                </Box>
                <Chip size="small" label={state.label} color={state.color} variant="outlined" />
            </Stack>

            {job.state === 'failed' ? (
                <Typography variant="body2" color="error" sx={{ mt: 0.75 }}>
                    Não conseguiu consultar o parceiro. Tente de novo mais tarde.
                </Typography>
            ) : result.seen != null ? (
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                    Viu {result.seen} venda(s), atualizou {result.updated || 0} resgate(s)
                    {result.unmatched ? `, ${result.unmatched} sem resgate nosso` : ''}.
                </Typography>
            ) : job.state === 'active' ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Consultando o parceiro agora.
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Ainda não rodou.
                </Typography>
            )}

            {updated.length ? (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        O que atualizou
                    </Typography>
                    {updated.map((item, index) => (
                        <Typography key={`${item.rescueId}-${index}`} variant="body2">
                            {describeItem(item)}
                        </Typography>
                    ))}
                </Box>
            ) : null}

            {skipped.length ? (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        O que não casou
                    </Typography>
                    {skipped.map((item, index) => (
                        <Typography key={`${item.code}-${index}`} variant="body2" color="text.secondary">
                            {describeItem(item)}
                        </Typography>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

export default function VerificationJobsDialog({ open, onClose }) {
    const { data, isFetching, mutate } = useFetch(open ? 'rescues/verification-jobs' : null);

    const onSocketUpdate = useCallback(payload => {
        if (payload?.jobs) {
            mutate(
                { data: { jobs: payload.jobs, counts: payload.counts, nextRuns: payload.nextRuns } },
                { revalidate: false },
            );
            return;
        }
        mutate();
    }, [mutate]);

    const { connected } = useReconciliationSocket({ enabled: open, onUpdate: onSocketUpdate });

    const jobs = data?.jobs ?? [];
    const nextRuns = data?.nextRuns ?? [];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    Verificações
                    <Tooltip title={connected ? 'Atualiza na hora' : 'Desconectado — use Atualizar'}>
                        <Chip
                            size="small"
                            icon={<CircleRounded sx={{ fontSize: '10px !important' }} />}
                            label={connected ? 'Ao vivo' : 'Offline'}
                            color={connected ? 'success' : 'default'}
                            variant={connected ? 'filled' : 'outlined'}
                        />
                    </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary" fontWeight={400}>
                    Cada meia hora o sistema pergunta à Awin e à Lomadee se nasceu venda com o código do app.
                    A Awin também avisa na hora. A lista atualiza sozinha.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {nextRuns.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Próximas consultas:{' '}
                        {nextRuns
                            .map(item => `${item.broker === 'lomadee' ? 'Lomadee' : 'Awin'} ${formatDateTime(item.next)}`)
                            .join(' · ')}
                    </Typography>
                ) : null}

                {isFetching && !data ? (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress size={28} />
                    </Stack>
                ) : jobs.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                        Ainda não há verificação registrada. Elas aparecem depois da primeira consulta.
                    </Typography>
                ) : (
                    <Stack spacing={2} divider={<Divider flexItem />}>
                        {jobs.map(job => (
                            <JobCard key={job.id} job={job} />
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => mutate()}>Atualizar</Button>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
